import { useEffect, useState } from 'react';
import { Box, Flex, Link, Text } from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

import { accent, font, fontSize, neutral, text } from 'styles/revampTokens';

import { formatDue, statusLabel, statusTone } from './format';
import { groupTasks, primaryTaskId, runTitle, TaskGroup } from './groupTasks';
import { listTasks, maestroErrorMessage } from './maestroClient';
import { pageTitle, PlanningPage } from './TasksChrome';
import { HubTask } from './types';

const attentionTotal = (tasks: HubTask[]) =>
  tasks.reduce((total, task) => total + (task.attention_count || 0), 0);

const commentTotal = (tasks: HubTask[]) =>
  tasks.reduce((total, task) => total + (task.comment_count || 0), 0);

const RunCard = ({ group }: { group: TaskGroup }) => {
  const [open, setOpen] = useState(true);
  const first = group.tasks[0];
  const overdue = group.tasks.some((task) => task.overdue && group.current);
  const attention = attentionTotal(group.tasks);
  const comments = commentTotal(group.tasks);
  const href = `/tasks/${primaryTaskId(group)}`;
  const title = runTitle(group.tasks);

  return (
    <Box
      bg={neutral.surface}
      border={`1px solid ${neutral.border}`}
      borderRadius='8px'
      p='18px 20px'
      _hover={{ borderColor: accent.solid }}
    >
      <Flex justify='space-between' gap={4} align='flex-start'>
        <Box>
          <Link
            as={RouterLink}
            to={href}
            fontFamily={font.display}
            fontSize='15px'
            color={text.primary}
            fontWeight={600}
            _hover={{ color: accent.solid }}
          >
            {title}
          </Link>
          <Text mt={1} fontSize={fontSize.label} color={text.secondary}>
            {first.cycle_label}
            {first.period ? ` · ${first.period}` : ''}
            {' · '}
            Due {formatDue(first.due_at)}
            {overdue ? ' · Overdue' : ''}
          </Text>
        </Box>
        <Flex align='center' gap={2} color={text.secondary} flexShrink={0}>
          <Text fontSize={fontSize.label}>
            {group.tasks.length === 1
              ? statusLabel(group.tasks[0].status)
              : `${group.tasks.length} items`}
          </Text>
          <Flex
            as='button'
            type='button'
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} items in ${title}`}
            align='center'
            justify='center'
            boxSize='28px'
            borderRadius='6px'
            color={text.primary}
            _hover={{ bg: neutral.surfaceHover }}
            onClick={() => setOpen((current) => !current)}
          >
            <Box as={open ? FiChevronDown : FiChevronRight} aria-hidden />
          </Flex>
        </Flex>
      </Flex>
      {open && (
        <Flex mt={3} gap={2} wrap='wrap'>
          {group.tasks.map((task) => {
            const tone = statusTone(task.status);
            return (
              <Link
                key={task.id}
                as={RouterLink}
                to={`/tasks/${task.id}`}
                px={3}
                py={1}
                borderRadius='8px'
                bg={tone.bg}
                color={tone.color}
                fontSize={fontSize.label}
                fontWeight={600}
                _hover={{ opacity: 0.85 }}
              >
                {task.dimension_item_name} · {statusLabel(task.status)}
              </Link>
            );
          })}
        </Flex>
      )}
      {attention > 0 && (
        <Text mt={3} fontSize={fontSize.label} color={text.secondary}>
          {attention} {attention === 1 ? 'item needs' : 'items need'} attention
        </Text>
      )}
      {comments > 0 && (
        <Text
          mt={attention > 0 ? 1 : 3}
          fontSize={fontSize.label}
          color={accent.solid}
          fontWeight={600}
        >
          {comments === 1 ? '1 comment' : `${comments} comments`}
        </Text>
      )}
    </Box>
  );
};

const Section = ({ heading, groups }: { heading: string; groups: TaskGroup[] }) => {
  if (groups.length === 0) return null;
  return (
    <Box mt={8}>
      <Text fontSize={fontSize.label} fontWeight={600} color={text.secondary} mb={3}>
        {heading}
      </Text>
      <Flex direction='column' gap={3}>
        {groups.map((group) => (
          <RunCard key={group.runId} group={group} />
        ))}
      </Flex>
    </Box>
  );
};

export const TaskInboxScreen = ({ tasks, error }: { tasks: HubTask[] | null; error: string }) => {
  const grouped = tasks ? groupTasks(tasks) : null;

  return (
    <PlanningPage centered>
      <Box flex='1' minH={0} overflow='auto'>
        <Text {...pageTitle}>Tasks</Text>
        <Text mt='9px' fontSize='13px' color={text.secondary}>
          Current and finished planning work assigned to you.
        </Text>
        {error && (
          <Text mt={6} fontSize={fontSize.body} color={text.primary}>
            {error}
          </Text>
        )}
        {!error && tasks === null && (
          <Text mt={6} fontSize={fontSize.body} color={text.secondary}>
            Loading your tasks…
          </Text>
        )}
        {grouped && grouped.current.length === 0 && grouped.past.length === 0 && (
          <Text mt={6} fontSize={fontSize.body} color={text.secondary}>
            You have no planning tasks.
          </Text>
        )}
        {grouped && <Section heading='Current' groups={grouped.current} />}
        {grouped && <Section heading='Past' groups={grouped.past} />}
      </Box>
    </PlanningPage>
  );
};

const TaskInbox = () => {
  const [tasks, setTasks] = useState<HubTask[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'My tasks — Blox';
    let cancelled = false;
    listTasks()
      .then((rows) => {
        if (!cancelled) setTasks(rows);
      })
      .catch((loadError: unknown) => {
        if (!cancelled) setError(maestroErrorMessage(loadError, 'Your tasks could not be loaded.'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <TaskInboxScreen tasks={tasks} error={error} />;
};

export default TaskInbox;
